import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnInit,
	Output,
	SimpleChanges,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import dayjs from 'dayjs';
import { LegacyApiService } from '@lmc-app/core/services/legacy-api.service';
import { ApplicationService } from '@lmc-app/core/services/application.service';
import { UserService } from '@lmc-app/core/services/user.service';
import { isExternalUser, User } from '@lmc-app/shared/interface/user.interface';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { StageChangeNoteFormComponent } from '@lmc-app/shared/components/stage-change-note-form/stage-change-note-form.component';
import { Subscription } from 'rxjs';
import { SharedServiceService } from '@lmc-app/shared-service.service';

@Component({
	selector: 'app-summary-bar',
	templateUrl: './summary-bar.component.html',
	styleUrls: ['./summary-bar.component.scss'],
})
export class SummaryBarComponent implements OnInit, OnChanges {
	@Input() showSummaryBar!: boolean;
	@Input() selectedTab!: string;
	private parentSub = new Subscription();
	protected expandSummaryPanel: boolean = false;

	protected voeStatus: string | null = null;
	protected nextScheduleCall: { purpose: string, time: string } | null = null;
	private stageName: any;
	protected stageOptions: { value: any; label: string }[] = [];

	protected user!: User;
	protected isStageUpdating: boolean = false;
	protected isLoading: boolean = true;
	@Input() application: any;
	@Output() applicationChange = new EventEmitter();
	@Output() consentClick = new EventEmitter();
	@Input() savedDate!: Date;

	protected isStageEditMode: boolean = false;
	protected stageFormControl = new FormControl({ value: '', disabled: true });
	protected readonly isExternalUser = isExternalUser;
	private sharedStageSubscription: Subscription;
	constructor(
		private readonly apiService: LegacyApiService,
		private readonly applicationService: ApplicationService,
		private readonly userService: UserService,
		private readonly bottomSheet: MatBottomSheet,
		private readonly sharedService: SharedServiceService
	) {
		this.sharedStageSubscription = this.sharedService.currentUpdatedStage$.subscribe(stage => {
			this.stageFormControl.setValue(stage || '');
		});
	}

	public ngOnChanges(changes: SimpleChanges) {
		if (changes.application?.currentValue) {
			this.application = changes.application?.currentValue;
			this.stageFormControl.setValue(this.application?.Stage || '');
			this.getNextCall();
			this.getVoeStatus();
			this.isLoading = false;
		}
		if (changes.selectedTab?.currentValue) {
			this.expandSummaryPanel = this.selectedTab === 'activities';
		}
	}

	public ngOnInit(): void {
		// The VOE status comes from the notes, so look again whenever one is added, edited or deleted
		this.parentSub.add(this.apiService.notesChanged$.subscribe(() => this.getVoeStatus()));
		this.userService.user$.subscribe((user) => {
			this.user = user;
		});
		this.apiService.getModuleField('Deals', 'Stage').subscribe((res) => {
			this.stageName = res.pick_list_values;
			this.stageOptions = this.stageName.map((stage: any) => ({
				value: stage.display_value,
				label: stage.display_value,
			}));
		});
	}

	private getVoeStatus(): void {
		if (!this.application?.id) {
			return;
		}
		this.parentSub.add(this.applicationService.getVoeStatus(this.application.id).subscribe({
			next: (res) => this.voeStatus = res.status,
			error: (error) => {
				console.error(error);
				this.voeStatus = null;
			},
		}));
	}

	/**
	 * Complete only when the application has emergency contacts and all of them are marked completed, across
	 * the client's and the co-applicant's; N/A when there are none
	 */
	protected get ecCompleteStatus(): string {
		const ecs = [...(this.application?.EMERGANCY_CONTACTS || []), ...(this.application?.Co_Applicant_EC || [])];
		if (!ecs.length) {
			return 'N/A';
		}
		return ecs.every((ec: { _completed: boolean }) => ec._completed) ? 'Completed' : 'Incomplete';
	}

	private getNextCall(): void {
		this.parentSub.add(this.applicationService
			.queryApplicationCalls(this.application.id, dayjs().toISOString(), undefined, "SCHEDULED", undefined)
			.subscribe({
				next: (res) => {
					this.nextScheduleCall = null;
					if (res && res.length) {
						let purpose = "";
						switch (res[0].purpose) {
							case "SCHEDULE_A_CALL":
								purpose = "scheduled"
								break;
							case "READY_FOR_LOAN_INTERVIEW":
								purpose = "interview"
								break;
							case "ADMINISTRATIVE":
								purpose = "administrative"
								break;
							default:
								break;
						}
						this.nextScheduleCall = { purpose, time: dayjs(res[0].dateTime).format('YYYY-MM-DD h:mm A') };
					}
				},
			}));
	}

	private refreshApplication(): void {
		this.isLoading = true;
		this.applicationChange.emit(this.application);
	}

	protected calculateAge(dateOfBirth: string): number | null {
		if (!dateOfBirth) return null;

		const birthDate = dayjs(dateOfBirth);
		const today = dayjs();

		if (!birthDate.isValid()) return null;

		return today.diff(birthDate, 'year');
	}

	protected formatPhoneNumber(phone: string): string {
		if (!phone) return 'N/A';

		const cleaned = phone.replace(/\D/g, '');
		if (cleaned.length < 10) return phone;
		const areaCode = cleaned.substring(0, 3);
		const prefix = cleaned.substring(3, 6);
		const lineNumber = cleaned.substring(6, 10);
		return `(${areaCode}) ${prefix}-${lineNumber}`;
	}

	protected enableStageEdit(): void {
		this.isStageEditMode = true;
		this.stageFormControl.enable();
	}

	protected saveStageChange(): void {
		const newValue = this.stageFormControl.value!;

		switch (newValue) {
			case "Co-App Obtained":
			case "Co-App Budget Assessment":
			case "Agreements":
			case "Validation":
			case "Funded":
			case "Refinanced":
			case "Rapid - Agreement":
			case "Underwriting Checklist":
				this.onStageChangeNoteSubmit(newValue, null);
				break;
			default:
				// Open bottom sheet for note input
				const bottomSheetRef = this.bottomSheet.open(StageChangeNoteFormComponent, {
					data: {
						newStage: newValue,
						applicationId: this.application.id,
					},
					disableClose: false,
				});

				bottomSheetRef.afterDismissed().subscribe((result) => {
					if (result && result.note) {
						// User submitted the form
						this.onStageChangeNoteSubmit(newValue, result.note);
					} else {
						// User cancelled
						this.cancelStageEdit();
					}
				});
				break;
		}
	}

	private onStageChangeNoteSubmit(newStage: string, noteData: any): void {
		this.isStageUpdating = true;

		// Retry getting application to make sure the Stage_History_ID is ready
		if (!this.application.Stage_History_ID) {
			this.applicationService.getApplication(this.application.id).subscribe({
				next: (res) => {
					this.application.Stage_History_ID = res.Stage_History_ID;
					this.updateApplicationStageWithNote(
						this.application.id,
						newStage,
						noteData,
						this.application.Stage_History_ID,
						this.user.zoho_owner_id,
						this.application.Deal_Name,
						this.user.full_name
					);
				},
				error: (error) => {
					console.error(error);
				},
			});
		}
		else {
			this.updateApplicationStageWithNote(
				this.application.id,
				newStage,
				noteData,
				this.application.Stage_History_ID,
				this.user.zoho_owner_id,
				this.application.Deal_Name,
				this.user.full_name
			);
		}
	}

	private updateApplicationStageWithNote(
		applicationId: string,
		stage: string,
		noteData: any,
		stageHistoryId: string,
		zohoUserId: string,
		dealName: string,
		createdBy: string
	): void {
		this.applicationService
			.updateApplicationStageWithNote(
				applicationId,
				stage,
				noteData,
				stageHistoryId,
				zohoUserId,
				dealName,
				createdBy
			)
			.subscribe({
				next: (res) => {
					if (res.statusCode === 200) {
						this.application.Stage = stage;
						this.isStageEditMode = false;
						this.stageFormControl.disable();
						this.isStageUpdating = false;
						this.refreshApplication();
					} else {
						console.error('Stage update failed:', res);
						this.cancelStageEdit();
						this.isStageUpdating = false;
					}
				},
				error: (error) => {
					console.error('Stage update error:', error);
					this.cancelStageEdit();
					this.isStageUpdating = false;
				},
			});
	}

	protected cancelStageEdit(): void {
		this.isStageEditMode = false;
		this.stageFormControl.setValue(this.application.Stage);
		this.stageFormControl.disable();
	}

	get displayAmount(): number | null {
		return !isExternalUser(this.user)
			? this.application?.Amount
			: this.application?.Agency_Amount;
	}

	protected telCall(): void {
		window.open(`tel:${this.application?.Mobile || this.application?.Phone}`, '_self');
	}

	public ngOnDestroy(): void {
		this.parentSub.unsubscribe();
		this.sharedStageSubscription.unsubscribe();
	}
}
